import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom'; // <-- Imported useSearchParams
import { axiosInstance } from '@/App';
import { Search, ChevronUp, ChevronDown, Trash2, ShoppingCart, LogIn, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import Layout from '@/components/Layout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const HomePage = () => {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingItems, setAddingItems] = useState(new Set());
  const [addedItems, setAddedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [itemQuantities, setItemQuantities] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // URL-driven state for the Quick View Modal
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedItemId = searchParams.get('item');
  const selectedItem = items.find(i => i.item_id === selectedItemId) || null;

  const fetchItems = async () => {
    try {
      const response = await axiosInstance.get('/items?limit=500');
      setItems(response.data);
      if (response.data.length === 0) {
        await axiosInstance.post('/seed-items');
        const newResponse = await axiosInstance.get('/items?limit=500');
        setItems(newResponse.data);
      }
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      let fetchedCategories = response.data;
      
      // Hardcode 'Pulses' to appear first
      if (fetchedCategories.includes('Pulses')) {
        // Remove 'Pulses' from its current position
        fetchedCategories = fetchedCategories.filter(cat => cat !== 'Pulses');
        // Add 'Pulses' to the very beginning of the array
        fetchedCategories.unshift('Pulses');
      }
      
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadCartItems = async () => {
    try {
      const response = await axiosInstance.get('/cart');
      const cartItems = response.data.items || [];
      const addedItemIds = new Set(cartItems.map(item => item.item_id));
      setAddedItems(addedItemIds);
      const quantitiesFromCart = cartItems.reduce((acc, cartItem) => {
        acc[cartItem.item_id] = cartItem.quantity || 1;
        return acc;
      }, {});
      setItemQuantities(prev => ({ ...prev, ...quantitiesFromCart }));
    } catch (error) {
      console.error('Failed to load cart items');
    }
  };

  const checkAuthAndLoadData = useCallback(async () => {
    try {
      const authResponse = await axiosInstance.get('/auth/me');
      const currentUser = authResponse.data;
      setUser(currentUser);
      if (currentUser) loadCartItems();
    } catch (error) {
      console.log("Guest user");
    } finally {
      fetchItems();
      fetchCategories();
    }
  }, []);

  useEffect(() => {
    checkAuthAndLoadData();
  }, [checkAuthAndLoadData]);

  const handleLoginPrompt = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({
      client_id: clientId, redirect_uri: redirectUri, response_type: "id_token",
      scope: "openid email profile", nonce: Math.random().toString(36).substring(2), prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const getItemQuantity = (itemId) => itemQuantities[itemId] || 1;

  const updateCartItemQuantity = async (item, nextQuantity) => {
    try {
      const cartResponse = await axiosInstance.get('/cart');
      const currentCart = cartResponse.data.items || [];
      const existingItemIndex = currentCart.findIndex(cartItem => cartItem.item_id === item.item_id);
      let updatedCart = [...currentCart];
      if (existingItemIndex >= 0) {
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex], quantity: nextQuantity, total: nextQuantity * updatedCart[existingItemIndex].rate,
        };
      } else {
        updatedCart = [...currentCart, {
            item_id: item.item_id, item_name: item.name, rate: item.rate, unit: item.unit || '1 kg', quantity: nextQuantity, total: item.rate * nextQuantity,
        }];
        setAddedItems(prev => new Set([...prev, item.item_id]));
      }
      await axiosInstance.put('/cart', { items: updatedCart });
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const setQuantity = (item, nextQuantity) => {
    setItemQuantities(prev => ({ ...prev, [item.item_id]: nextQuantity }));
    if (addedItems.has(item.item_id)) updateCartItemQuantity(item, nextQuantity);
  };

  const increaseQuantity = (item, e) => {
    if (e) e.stopPropagation(); // Prevent modal from opening
    setQuantity(item, getItemQuantity(item.item_id) + 1);
  };
  
  const decreaseQuantity = (item, e) => {
    if (e) e.stopPropagation(); // Prevent modal from opening
    const currentQty = getItemQuantity(item.item_id);
    if (currentQty > 1) {
      setQuantity(item, currentQty - 1);
    } else {
      removeFromCart(item, e);
    }
  };

  const addToCart = async (item, e) => {
    if (e) e.stopPropagation(); // Prevent modal from opening
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setAddingItems(prev => new Set([...prev, item.item_id]));
    setItemQuantities(prev => ({ ...prev, [item.item_id]: 1 }));

    try {
      const updatedCart = items
        .filter(i => addedItems.has(i.item_id) || i.item_id === item.item_id)
        .map(i => {
          const quantity = i.item_id === item.item_id ? 1 : getItemQuantity(i.item_id);
          return { item_id: i.item_id, item_name: i.name, rate: i.rate, unit: i.unit || '1 kg', quantity, total: i.rate * quantity };
        });

      await axiosInstance.put('/cart', { items: updatedCart });
      toast.success(`${item.name} added to cart!`);
      setAddedItems(prev => new Set([...prev, item.item_id]));
    } catch (error) {
      toast.error('Failed to add item to cart');
      setAddedItems(prev => { const newSet = new Set(prev); newSet.delete(item.item_id); return newSet; });
    } finally {
      setAddingItems(prev => { const newSet = new Set(prev); newSet.delete(item.item_id); return newSet; });
    }
  };

  const removeFromCart = async (item, e) => {
    if (e) e.stopPropagation(); // Prevent modal from opening
    setAddingItems(prev => new Set([...prev, item.item_id]));
    try {
      const cartResponse = await axiosInstance.get('/cart');
      const updatedCart = (cartResponse.data.items || []).filter(cartItem => cartItem.item_id !== item.item_id);
      await axiosInstance.put('/cart', { items: updatedCart });
      toast.success(`${item.name} removed from cart!`);
      setAddedItems(prev => { const newSet = new Set(prev); newSet.delete(item.item_id); return newSet; });
      setItemQuantities(prev => ({ ...prev, [item.item_id]: 1 }));
    } catch (error) {
      toast.error('Failed to remove item from cart');
    } finally {
      setAddingItems(prev => { const newSet = new Set(prev); newSet.delete(item.item_id); return newSet; });
    }
  };

  const filteredItems = searchQuery.trim() 
    ? items.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  const filteredItemsByCategory = categories.reduce((acc, category) => {
    acc[category] = filteredItems.filter(item => item.category === category);
    return acc;
  }, {});

  return (
    <Layout user={user} setUser={setUser}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search for items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-zinc-300 focus:border-emerald-500 focus:ring-emerald-500 font-secondary bg-white shadow-sm"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-zinc-600 font-secondary">Loading products...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {categories.map(category => {
              const categoryItems = filteredItemsByCategory[category] || [];
              if (categoryItems.length === 0) return null;

              return (
                <div key={category}>
                  <div className="flex items-center gap-3 mb-6">
                    <h3 className="text-2xl font-bold font-primary text-emerald-950">{category}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
                    {categoryItems.map(item => (
                      <div 
                        key={item.item_id} 
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set('item', item.item_id);
                          setSearchParams(params);
                        }}
                        className="relative bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer flex flex-col"
                      >
                        <div className="h-32 md:h-48 bg-zinc-100 overflow-hidden relative">
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=300&fit=crop'; }}
                          />
                        </div>
                        
                        <div className="p-3 md:p-4 flex flex-col flex-1">
                          <h4 className="font-primary font-bold text-emerald-950 text-sm md:text-base mb-1.5 leading-tight break-words">{item.name}</h4>
                          
                          <div className="mb-auto">
                            <span className="inline-flex items-center justify-center border border-zinc-300 bg-zinc-50 text-zinc-600 text-[10px] md:text-xs px-2 py-0.5 rounded-full font-medium">
                              {item.unit || '1 kg'}
                            </span>
                          </div>

                          <div className="mt-3 flex flex-col gap-2">
                            <div className="flex items-center gap-1">
                              <p className="flex items-center text-sm md:text-lg font-bold text-emerald-700 font-primary">
                                <IndianRupee className="w-3.5 h-3.5 md:w-4 md:h-4 mr-0.5" strokeWidth={2.5} />
                                {item.rate}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-1">
                              {addedItems.has(item.item_id) ? (
                                <>
                                  <div className="flex items-center justify-center gap-1 bg-zinc-100 rounded px-2 py-1.5 flex-1">
                                    <button onClick={(e) => decreaseQuantity(item, e)} className="p-1 hover:bg-zinc-200 rounded">
                                      <ChevronDown className="h-4 w-4 text-zinc-600" />
                                    </button>
                                    <span className="text-sm md:text-base font-bold text-emerald-600 px-2 min-w-[30px] text-center">{getItemQuantity(item.item_id)}</span>
                                    <button onClick={(e) => increaseQuantity(item, e)} className="p-1 hover:bg-zinc-200 rounded">
                                      <ChevronUp className="h-4 w-4 text-zinc-600" />
                                    </button>
                                  </div>
                                  <Button onClick={(e) => removeFromCart(item, e)} disabled={addingItems.has(item.item_id)} variant="outline" size="icon" className="h-9 w-9 md:h-10 md:w-10 border-zinc-300 text-zinc-700 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <Button onClick={(e) => addToCart(item, e)} disabled={addingItems.has(item.item_id)} className="font-secondary text-xs md:text-sm bg-emerald-600 hover:bg-emerald-700 text-white w-full shadow-sm" size="sm">
                                  {addingItems.has(item.item_id) ? (
                                    <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>Adding</>
                                  ) : (
                                    <>Add to Cart</>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {searchQuery.trim() && filteredItems.length === 0 && (
              <div className="text-center py-20">
                <Search className="h-16 w-16 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-600 font-secondary text-lg mb-2">No items found</p>
                <p className="text-zinc-500 font-secondary text-sm">Try searching with different keywords</p>
              </div>
            )}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-20">
            <ShoppingCart className="h-16 w-16 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-600 font-secondary text-lg">No items available</p>
          </div>
        )}
      </div>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-bold font-primary text-emerald-950">
              Sign in to continue
            </DialogTitle>
            <DialogDescription className="text-center text-zinc-500 font-secondary">
              You need to be signed in to add items to your cart and complete your order.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <LogIn className="w-8 h-8 text-emerald-900" />
            </div>
            <Button onClick={handleLoginPrompt} className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-12 text-base font-primary font-medium">
              Sign in with Google
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick View Product Modal */}
      <Dialog 
        open={!!selectedItem} 
        onOpenChange={(open) => {
          if (!open) {
            const params = new URLSearchParams(searchParams);
            params.delete('item');
            setSearchParams(params);
          }
        }}
      >
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl [&>button]:bg-white [&>button]:text-zinc-900 [&>button]:hover:bg-zinc-100 [&>button]:opacity-100 [&>button]:rounded-full [&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:shadow-md [&>button]:z-50 [&>button]:top-4 [&>button]:right-4">
          {selectedItem && (
            <div className="flex flex-col bg-white">
              {/* Product Image Wrapper */}
              <div className="relative w-full h-64 sm:h-72 bg-zinc-100">
                <img 
                  src={selectedItem.image_url} 
                  alt={selectedItem.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=300&fit=crop'; }}
                />
                <Badge className="absolute top-4 left-4 bg-white/90 text-emerald-950 hover:bg-white border-0 shadow-sm backdrop-blur-sm">
                  {selectedItem.category}
                </Badge>
              </div>

              {/* Image Disclaimer */}
              <div className="w-full bg-zinc-50 border-b border-zinc-100 py-1.5 px-4 text-center">
                 <p className="text-[10px] text-zinc-500 font-secondary italic">
                   * The images shown are for representational purposes only.
                 </p>
              </div>

              {/* Product Info Content */}
              <div className="px-6 pb-6 pt-5">
                <DialogHeader className="text-left space-y-0 pr-6">
                  <div className="flex justify-between items-start gap-4">
                    <DialogTitle className="text-2xl font-bold font-primary text-emerald-950 leading-tight">
                      {selectedItem.name}
                    </DialogTitle>
                  </div>
                  
                  {/* Dynamic Fallback Description */}
                  <DialogDescription className="text-zinc-500 font-secondary mt-2 text-sm">
                    {selectedItem.description || `Fresh and high-quality ${selectedItem.name.toLowerCase()} sourced locally. Perfect for your daily household and grocery needs.`}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-6 flex items-center justify-between border-y border-zinc-100 py-4">
                  <div>
                    <p className="text-xs text-zinc-500 font-primary uppercase tracking-wider font-bold mb-1">Unit</p>
                    <span className="inline-flex items-center justify-center border border-zinc-300 bg-zinc-50 text-zinc-700 text-xs px-3 py-1 rounded-full font-medium">
                      {selectedItem.unit || '1 kg'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-500 font-primary uppercase tracking-wider font-bold mb-1">Price</p>
                    <p className="flex items-center justify-end text-2xl font-bold text-emerald-700 font-primary">
                      <IndianRupee className="w-5 h-5 mr-0.5" strokeWidth={2.5} />
                      {selectedItem.rate}
                    </p>
                  </div>
                </div>

                {/* Big Add To Cart / Quantity Selector */}
                <div className="mt-6">
                  {addedItems.has(selectedItem.item_id) ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-xl p-2">
                        <button onClick={(e) => decreaseQuantity(selectedItem, e)} className="p-3 bg-white hover:bg-zinc-100 border border-zinc-200 rounded-lg transition-colors">
                          <ChevronDown className="h-5 w-5 text-zinc-700" />
                        </button>
                        <div className="flex flex-col items-center">
                          <span className="text-xl font-bold text-emerald-700">{getItemQuantity(selectedItem.item_id)}</span>
                          <span className="text-[10px] text-zinc-500 font-secondary font-medium uppercase">In Cart</span>
                        </div>
                        <button onClick={(e) => increaseQuantity(selectedItem, e)} className="p-3 bg-white hover:bg-zinc-100 border border-zinc-200 rounded-lg transition-colors">
                          <ChevronUp className="h-5 w-5 text-zinc-700" />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between px-1">
                        <span className="text-sm font-medium text-zinc-500">Total item price:</span>
                        <span className="font-bold text-emerald-700 flex items-center">
                          <IndianRupee className="w-3.5 h-3.5 mr-0.5" />
                          {(selectedItem.rate * getItemQuantity(selectedItem.item_id)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      onClick={(e) => addToCart(selectedItem, e)} 
                      disabled={addingItems.has(selectedItem.item_id)} 
                      className="w-full h-14 text-base font-primary font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md rounded-xl"
                    >
                      {addingItems.has(selectedItem.item_id) ? (
                        <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Adding to Cart...</>
                      ) : (
                        <><ShoppingCart className="mr-2 h-5 w-5" /> Add to Cart</>
                      )}
                    </Button>
                  )}
                </div>

              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default HomePage;